import type { DrawingCatalogEntry, DrawingCategoryId, DrawingType } from "./types";

const lineDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "vertical-line",
    name: "Vertical line",
    category: "lines",
    requiredPoints: 1,
    parameters: [],
  },
  {
    drawingId: "horizontal-line",
    name: "Horizontal line",
    category: "lines",
    requiredPoints: 1,
    parameters: [],
  },
  {
    drawingId: "trendline",
    name: "Trendline",
    category: "lines",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "trend-by-angle",
    name: "Trendline by angle",
    category: "lines",
    requiredPoints: 2,
    parameters: [
      {
        name: "Angle",
        type: "number",
        defaultValue: 45,
        min: 0,
        max: 360,
      },
    ],
  },
];

const channelDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "equidistant-channel",
    name: "Equidistant channel",
    category: "channels",
    requiredPoints: 3,
    parameters: [],
  },
  {
    drawingId: "stddev-channel",
    name: "Standard deviation channel",
    category: "channels",
    requiredPoints: 2,
    parameters: [
      {
        name: "Deviations",
        type: "number",
        defaultValue: 2,
        min: 0.5,
        max: 5,
        step: 0.5,
      },
    ],
  },
  {
    drawingId: "regression-channel",
    name: "Regression channel",
    category: "channels",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "andrews-pitchfork",
    name: "Andrews' pitchfork",
    category: "channels",
    requiredPoints: 3,
    parameters: [],
  },
];

const fibonacciDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "fibonacci-retracement",
    name: "Fibonacci retracement",
    category: "fibonacci",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "fibonacci-time-zones",
    name: "Fibonacci time zones",
    category: "fibonacci",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "fibonacci-fan",
    name: "Fibonacci fan",
    category: "fibonacci",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "fibonacci-arcs",
    name: "Fibonacci arcs",
    category: "fibonacci",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "fibonacci-channel",
    name: "Fibonacci channel",
    category: "fibonacci",
    requiredPoints: 3,
    parameters: [],
  },
  {
    drawingId: "fibonacci-expansion",
    name: "Fibonacci expansion",
    category: "fibonacci",
    requiredPoints: 3,
    parameters: [],
  },
];

const gannDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "gann-line",
    name: "Gann line",
    category: "gann",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "gann-fan",
    name: "Gann fan",
    category: "gann",
    requiredPoints: 2,
    parameters: [
      {
        name: "Divisions",
        type: "number",
        defaultValue: 8,
        min: 2,
        max: 32,
      },
    ],
  },
  {
    drawingId: "gann-grid",
    name: "Gann grid",
    category: "gann",
    requiredPoints: 2,
    parameters: [
      {
        name: "Divisions",
        type: "number",
        defaultValue: 8,
        min: 2,
        max: 32,
      },
    ],
  },
];

const shapeDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "rectangle",
    name: "Rectangle",
    category: "shapes",
    requiredPoints: 2,
    parameters: [
      { name: "Fill", type: "boolean", defaultValue: true },
    ],
  },
  {
    drawingId: "ellipse",
    name: "Ellipse",
    category: "shapes",
    requiredPoints: 2,
    parameters: [
      { name: "Fill", type: "boolean", defaultValue: true },
    ],
  },
  {
    drawingId: "triangle",
    name: "Triangle",
    category: "shapes",
    requiredPoints: 3,
    parameters: [
      { name: "Fill", type: "boolean", defaultValue: true },
    ],
  },
];

const symbolDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "arrowed-line",
    name: "Arrow",
    category: "symbols-arrows",
    requiredPoints: 2,
    parameters: [],
  },
  {
    drawingId: "signal-arrow-up",
    name: "Arrow up",
    category: "symbols-arrows",
    requiredPoints: 1,
    parameters: [],
  },
  {
    drawingId: "signal-arrow-down",
    name: "Arrow down",
    category: "symbols-arrows",
    requiredPoints: 1,
    parameters: [],
  },
  {
    drawingId: "price-label",
    name: "Price label",
    category: "symbols-arrows",
    requiredPoints: 1,
    parameters: [],
  },
  {
    drawingId: "cycle-lines",
    name: "Cycle lines",
    category: "symbols-arrows",
    requiredPoints: 2,
    parameters: [],
  },
];

const textDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "text",
    name: "Text",
    category: "text",
    requiredPoints: 1,
    requiresTextPrompt: true,
    parameters: [],
  },
  {
    drawingId: "text-label",
    name: "Text label",
    category: "text",
    requiredPoints: 1,
    requiresTextPrompt: true,
    parameters: [],
  },
];

const elliottDrawings: DrawingCatalogEntry[] = [
  {
    drawingId: "elliott-motive-wave",
    name: "Elliott impulse wave (12345)",
    category: "elliott",
    requiredPoints: 5,
    parameters: [],
  },
  {
    drawingId: "elliott-corrective-wave",
    name: "Elliott correction wave (ABC)",
    category: "elliott",
    requiredPoints: 3,
    parameters: [],
  },
];

export const drawingCatalog: DrawingCatalogEntry[] = [
  ...lineDrawings,
  ...channelDrawings,
  ...fibonacciDrawings,
  ...gannDrawings,
  ...shapeDrawings,
  ...symbolDrawings,
  ...textDrawings,
  ...elliottDrawings,
];

export const drawingCatalogById: Record<DrawingType, DrawingCatalogEntry> =
  Object.fromEntries(
    drawingCatalog.map((entry) => [entry.drawingId, entry])
  ) as Record<DrawingType, DrawingCatalogEntry>;

const CATEGORY_LABELS: Record<DrawingCategoryId, string> = {
  lines: "Lines",
  channels: "Channels",
  fibonacci: "Fibonacci",
  gann: "Gann",
  shapes: "Shapes",
  "symbols-arrows": "Symbols & Arrows",
  text: "Text",
  elliott: "Elliott",
};

export function getCategoryLabel(id: DrawingCategoryId): string {
  return CATEGORY_LABELS[id];
}
